import {ShowWorkingTimeNoticeEvent, ShowWorkingTimeNoticeRequestEvent, WorkingTimeTimelineDayContent} from "../timelineTypes";
import {WorkingTimeNotice, WorkingTimeNoticeRequestResponseModel} from "@/shared/timr";
import {WorkingTimeTypeCategory} from "@/shared/timrTypes";
import {EVENT_WORKING_TIME_NOTICE_REQUEST_SHOW, EVENT_WORKING_TIME_NOTICE_SHOW} from "@/features/timeline/timelineConstants";

/**
 * Component to display working time notices and pending working time notice requests at the beginning of a timeline day.
 */
export class CombinedTimelineDayNotices extends HTMLElement {
	private _timelineDay: WorkingTimeTimelineDayContent;

	set timelineDay(value: WorkingTimeTimelineDayContent) {
		this._timelineDay = value;
	}

	public connectedCallback(): void {
		if (!this._timelineDay) {
			return;
		}

		const notices = this._timelineDay.workingTimeNotices.map(notice => this.createNoticeElement(notice));
		const noticeRequests = this._timelineDay.workingTimeNoticeRequests.map(noticeRequest => this.createNoticeRequestElement(noticeRequest));
		this.append(...notices, ...noticeRequests);
	}

	private createNoticeElement(notice: WorkingTimeNotice): HTMLElement {
		const noticeElement = this.createElement(notice.workingTimeTypeCategory, notice.workingTimeTypeName);
		noticeElement.addEventListener("click", () => this.showNotice(notice));
		return noticeElement;
	}

	/**
	 * A pending notice request looks like a notice, but with a white background and its status in front of the name.
	 */
	private createNoticeRequestElement(noticeRequest: WorkingTimeNoticeRequestResponseModel): HTMLElement {
		const category = noticeRequest.workingTimeType.category;
		const statusElement = document.createElement("span");
		statusElement.classList.add("label", `request-status-${noticeRequest.status}`, `request-category-${category}`, "mr-5");
		statusElement.textContent = messages["workingTimeRequestStatus." + noticeRequest.status];

		const noticeRequestElement = this.createElement(category, noticeRequest.workingTimeType.name, statusElement);
		noticeRequestElement.addEventListener("click", () => this.showNoticeRequest(noticeRequest.id));
		return noticeRequestElement;
	}

	/**
	 * Builds the notice panel; a given request status turns it into a notice request panel.
	 */
	private createElement(category: WorkingTimeTypeCategory, workingTimeTypeName: string, statusElement?: HTMLElement): HTMLElement {
		const noticeElement = document.createElement("div");
		noticeElement.classList.add("working-time-notice");
		noticeElement.tabIndex = 0;
		noticeElement.setAttribute("role", "button");

		const panelElement = document.createElement("div");
		panelElement.classList.add(
			"panel",
			"panel-working-time-notice",
			"border-color",
			`border-${category}`
		);

		const iconElement = document.createElement("span");
		iconElement.classList.add("fa-stack", "working-time-notice-icon", "position-left");
		iconElement.setAttribute("aria-hidden", "true");

		const calendarIconElement = document.createElement("i");
		calendarIconElement.classList.add("fal", "fa-calendar", "fa-stack-2x");

		const infoIconElement = document.createElement("i");
		infoIconElement.classList.add("fas", "fa-info", "fa-stack-1x");
		iconElement.append(calendarIconElement, infoIconElement);

		const textElement = document.createElement("span");
		textElement.classList.add("working-time-notice-text");
		textElement.textContent = workingTimeTypeName;

		if (statusElement) {
			panelElement.classList.add("panel-working-time-notice-request");
			panelElement.append(iconElement, statusElement, textElement);
		} else {
			panelElement.append(iconElement, textElement);
		}
		noticeElement.appendChild(panelElement);
		return noticeElement;
	}

	private showNotice(notice: WorkingTimeNotice): void {
		if (notice.requestId != null) {
			// A notice created by a request can only be changed through its request, so the request is opened instead
			this.showNoticeRequest(notice.requestId);
		} else {
			this.dispatchEvent(new CustomEvent<ShowWorkingTimeNoticeEvent>(EVENT_WORKING_TIME_NOTICE_SHOW, {
				detail: {workingTimeNotice: notice},
				bubbles: true
			}));
		}
	}

	private showNoticeRequest(workingTimeRequestId: number): void {
		this.dispatchEvent(new CustomEvent<ShowWorkingTimeNoticeRequestEvent>(EVENT_WORKING_TIME_NOTICE_REQUEST_SHOW, {
			detail: {workingTimeRequestId},
			bubbles: true
		}));
	}
}
